<!-- Typography.vue -->
<script setup lang="ts">
  import { ITypographyProps } from '@/types'

  const props = defineProps<ITypographyProps>()

  // Computa as classes dinâmicas com base nas propriedades
  const computedClasses = computed(() => {
    const classes = ['typography', `variant-${props.variant}`]

    // Se a cor foi fornecida, adiciona a classe com a cor fornecida
    if (props.color) {
      classes.push('custom-color')
    }

    return classes
  })
</script>

<template>
  <div :class="computedClasses">
    <slot :class="computedClasses" />
  </div>
</template>

<style scoped lang="scss">
  .typography {
    /* Estilos comuns para a tipografia */
    font-family: 'Inter';
    line-height: 1.5;
    margin: 0;
    padding: 0;
    white-space: pre-wrap;
  }

  .variant-title {
    font-size: 30px;
    font-weight: 600;
    line-height: 1.2;
    margin-bottom: 16px;
  }

  .variant-subtitle {
    font-size: 18px;
    font-weight: 500;
    line-height: 1.3;
    margin-bottom: 12px;
  }

  .variant-item {
    font-size: 16px;
    font-weight: 500;
    line-height: 1.2;
    align-content: center;
  }

  .variant-bullet-info-title {
    padding-inline: 27px;
    font-family: 'Inter';
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 0;
    line-height: 32px;
    opacity: 0.9;
  }

  .variant-filter-cell-description {
    font-family: 'Inter';
    font-size: 13px;
    font-weight: 400;
    letter-spacing: 0;
    line-height: 25px;
    color: #c0c0c1;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-line-clamp: 2;
  }

  .variant-bullet-info-value {
    padding-inline: 27px;
    font-family: 'Inter';
    font-size: 24px;
    font-weight: 600;
    letter-spacing: 0;
    line-height: 32px;
    opacity: 0.9;
  }

  .variant-bullet-info-description {
    padding-inline: 27px;
    font-family: 'Inter';
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0;
    line-height: 32px;
    opacity: 0.9;
  }

  .variant-regulation-details-title {
    color: #ffffff;
    font-size: 26px;
    font-weight: 500;
    font-size: 26px;
    line-height: 40px;
    letter-spacing: 0;
  }

  .variant-regulation-details-subtitle {
    color: #ffffff;
    font-size: 16px;
    font-weight: 600;
    line-height: 25px;
    letter-spacing: 0;
  }

  .variant-regulation-details-code {
    color: #04d200;
    font-size: 33px;
    font-weight: 700;
    letter-spacing: 0;
    line-height: 43px;
  }

  .variant-regulation-details-description {
    color: #ffffff;
    font-size: 15px;
    font-weight: 400;
    letter-spacing: 0;
    line-height: 25px;
  }

  .variant-regulation-details-subtitle,
  .variant-regulation-details-description {
    display: inline; /* Exibir os elementos inline */
  }

  .variant-regulation-details-link {
    color: #ffffff;
    font-size: 15px;
    font-weight: 400;
    letter-spacing: 0;
    line-height: 25px;
    text-decoration: underline;
    cursor: pointer;
  }

  .variant-regulation-topic {
    color: #ffffff99;
    font-size: 15px;
    font-weight: 500;
    line-height: 25px;
    letter-spacing: 0;
  }

  .variant-tooltip {
    color: #ffffff;
    font-size: 15px;
    font-weight: 400;
    line-height: 25px;
    letter-spacing: 0;
  }

  .regulation-details {
    color: #ffffff;
    font-size: 16px;
    font-weight: 400;
    letter-spacing: 0;
    line-height: 25px;
    align-self: stretch;
  }

  .variant-regulation-link {
    color: #ffffff;
    font-size: 16px;
    font-weight: 500;
    letter-spacing: 0;
    line-height: 25px;
    text-decoration: underline;
    cursor: pointer;
  }

  .variant-accordion-title {
    color: #ffffff;
    font-size: 16px;
    font-weight: 500;
    left: 20px;
    letter-spacing: 0;
    line-height: 25px;
    top: 14px;
    cursor: pointer;
  }

  .variant-accordion-description {
    color: #ffffffaa;
    font-size: 15px;
    font-weight: 400;
    left: 20px;
    letter-spacing: 0;
    line-height: 25px;
    top: 14px;
  }

  .variant-news-title {
    color: #ffffff;
    font-size: 24px;
    font-weight: 600;
    letter-spacing: 0;
    line-height: normal;
    opacity: 0.9;
  }

  .variant-news-description {
    align-self: stretch;
    text-align: justify;
    color: #ffffff;
    font-size: 16px;
    font-weight: 400;
    letter-spacing: 0;
    line-height: 26px;
    opacity: 0.9;
  }

  .variant-breadcrumb {
    color: #7c7c7c;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0;
    line-height: 22px;
    opacity: 0.9;
  }

  .variant-breadcrumb-current {
    color: #04d200;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0;
    line-height: 22px;
    opacity: 0.9;
  }

  .variant-modal-info-title {
    color: #ffffff;
    font-size: 18px;
    font-weight: 600;
    letter-spacing: 0;
    line-height: 32px;
    opacity: 0.9;
    margin-bottom: 25px;
  }

  .variant-modal-info-description {
    align-self: stretch;
    text-align: justify;
    color: #ffffff;
    font-size: 16px;
    font-weight: 400;
    letter-spacing: 0;
    line-height: 26px;
    opacity: 0.9;
  }

  .variant-body {
    font-size: 18px;
  }

  .custom-color {
    color: v-bind(color);
  }

  :slotted(div) {
    span {
      color: #ffffffaa;
      text-decoration: underline;
      cursor: pointer;
    }
    br {
      content: '';
      display: block;
      margin: 1rem 0;
    }
  }
</style>
